import React, { useState, useEffect } from 'react';
import { StyleSheet, View, TextInput, Pressable, Platform } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import NavigationBarButton from '@/components/Navigation/NavigationBarButton';
import { useTheme } from '@/context/ThemeContext';
import { useModelStore, useUIStore, useAuthStore, useModalStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import PropTypes from 'prop-types';
import useResponsiveness from '@/hooks/useResponsiveness';
import DropdownMenuLabel from '@/components/DropdownMenu/DropdownMenuLabel';
import Icon from '@/components/Icon';
import Badge from '@/components/Typography/Badge';
import Text from '@/components/Typography/Text';
import { canAccessPlusOnlyModel, isModelLockedForUser, openModelAccessPaywall } from '@/constants/utils/trial';

// Create styles outside component
const createStyles = (colors) => StyleSheet.create({
  menuItem: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  menuItemText: {
    color: colors.text_color_primary,
    fontSize: 14,
  },
  modelImages: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  modelImage: {
    width: 24,
    height: 24,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: colors.solid_color_primary,
  },
  modelImageOverlap: {
    marginLeft: -12, // Creates overlap effect
  },
  modelCount: {
    marginLeft: 4,
    fontSize: 14,
    color: colors.text_color_primary,
  },
  searchContainer: {
    padding: 0,
    position: 'relative',
    marginBottom: 5,
  },
  searchInput: {
    backgroundColor: colors.solid_color_tertiary,
    padding: 8,
    paddingLeft: 36,
    paddingRight: 38,
    borderRadius: 6,
    color: colors.text_color_primary,
  },
  searchIconContainer: {
    position: 'absolute',
    left: 12,
    top: '50%',
    transform: [{ translateY: -7 }],
    zIndex: 1,
  },
  clearIconContainer: {
    position: 'absolute',
    right: 14,
    top: '50%',
    transform: [{ translateY: -7 }],
    zIndex: 1,
    cursor: 'pointer',
  },
  titleContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  titleBadge: {
    marginLeft: 'auto',
  },
});

const ModelSelector = ({ modelIds, onChange, allowsMultiple }) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const pathname = usePathname();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedModelIds, setSelectedModelIds] = useState(modelIds || []);
  const { isXS } = useResponsiveness();
  const models = useModelStore(state => state.models);
  const featuredModelIds = useModelStore(state => state.featuredModelIds);
  const isLoadingFeaturedModels = useModelStore(state => state.isLoadingFeaturedModels);
  const isFetchedFeaturedModels = useModelStore(state => state.isFetchedFeaturedModels);
  const searchResults = useModelStore(state => state.searchResults);
  const isSearching = useModelStore(state => state.isSearching);
  const searchError = useModelStore(state => state.searchError);
  const fetchFeaturedModels = useModelStore(state => state.fetchFeaturedModels);
  const clearSearch = useModelStore(state => state.clearSearch);
  const searchModels = useModelStore(state => state.searchModels);
  const isSidebarVisible = useUIStore(state => state.isSidebarVisible);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const user = useAuthStore(state => state.user);
  const { openModal } = useModalStore(state => state.actions);
  const canUseGatedModels = canAccessPlusOnlyModel(user);
  const [pwaSafeAreaTop, setPwaSafeAreaTop] = useState(0);
  const pwaTopOffset = pwaSafeAreaTop + 24;

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    const readSafeArea = () => {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--pwa-safe-area-top') || '0';
      const parsed = parseInt(raw, 10);
      setPwaSafeAreaTop(Number.isNaN(parsed) ? 0 : parsed);
    };

    readSafeArea();
    window.addEventListener('resize', readSafeArea);
    return () => window.removeEventListener('resize', readSafeArea);
  }, []);

  // Get the selected models from the store
  const selectedModels = selectedModelIds.map(id => models[id]).filter(Boolean);
  const currentModel = allowsMultiple ? selectedModels[0] : models[selectedModelIds[0]];

  // Close dropdown when route changes
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  // Close dropdown when sidebar visibility changes
  useEffect(() => {
    setIsOpen(false);
  }, [isSidebarVisible]);

  // Initialize selectedModelIds when modelIds changes
  useEffect(() => {
    if (modelIds) {
      setSelectedModelIds(Array.isArray(modelIds) ? modelIds : [modelIds]);
    } else {
      setSelectedModelIds([]);
    }
  }, [modelIds]);

  // Fetch featured models on mount if not already loaded
  useEffect(() => {
    if (!isLoadingFeaturedModels && !isFetchedFeaturedModels) {
      try {
        fetchFeaturedModels();
      } catch (error) {
        console.error('Failed to fetch featured models:', error);
      }
    }
  }, [fetchFeaturedModels, isLoadingFeaturedModels, isFetchedFeaturedModels]);

  // Cleanup search when component unmounts
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      clearSearch();
    };
  }, []);

  // Handle search with debounce
  const handleSearch = (text) => {
    setSearchQuery(text);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Clear search if query is empty
    if (!text.trim()) {
      clearSearch();
      return;
    }
    
    const timeout = setTimeout(() => {
      if (text.trim()) {
        searchModels(text);
      }
    }, 500);
    
    setSearchTimeout(timeout);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    clearSearch();
  };

  const styles = React.useMemo(() => createStyles(colors), [colors]);

  // Handle dropdown state changes
  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open) {
      // Clear search when closing
      setSearchQuery('');
      clearSearch();
      if (onChange) {
        // When dropdown closes, call onChange with current model(s)
        if (allowsMultiple) {
          onChange(selectedModels);
        } else {
          onChange(currentModel || null);
        }
      }
    }
  };

  // Handle model selection
  const handleModelSelect = (model) => {
    if (isModelLockedForUser(user, model)) {
      setIsOpen(false);
      openModelAccessPaywall(openModal, { model });
      return;
    }

    if (allowsMultiple) {
      const newSelectedIds = selectedModelIds.includes(model.id)
        ? selectedModelIds.filter(id => id !== model.id)
        : [...selectedModelIds, model.id];
      
      setSelectedModelIds(newSelectedIds);
      const newSelectedModels = newSelectedIds.map(id => models[id]).filter(Boolean);
      
      if (onChange) {
        onChange(newSelectedModels);
      }
    } else {
      if (onChange) {
        onChange(model);
      }
      router.push(`/m/${model.id}`);
    }
  };

  // Handle view all models
  const handleViewAllModels = () => {
    if (onChange) {
      onChange(allowsMultiple ? [] : null);
    }
    if (!allowsMultiple) {
      router.push('/models');
    }
  };

  // Render model images for multiple selection
  const renderModelImages = () => {
    if (!allowsMultiple || selectedModels.length === 0) return null;

    const visibleModels = selectedModels.slice(0, 3);
    const remainingCount = selectedModels.length - 3;

    return (
      <View style={styles.modelImages}>
        {visibleModels.map((model, index) => (
          <View
            key={model.id}
            style={[
              styles.modelImage,
              index > 0 && styles.modelImageOverlap
            ]}
          >
            <img
              src={model.logoPath}
              alt={model.name}
              style={{ width: '100%', height: '100%', borderRadius: '50%' }}
            />
          </View>
        ))}
        {remainingCount > 0 && (
          <span style={styles.modelCount}>
            +{remainingCount}
          </span>
        )}
      </View>
    );
  };

  if (isLoadingFeaturedModels) {
    return null;
  }

  // Get featured models from IDs
  const featuredModels = searchQuery.trim()
    ? searchResults.filter(Boolean)
    : featuredModelIds.map(id => models[id]).filter(Boolean);

  return (
    <DropdownMenu.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenu.Trigger>
        <NavigationBarButton 
          title={!allowsMultiple ? (currentModel?.name || t('nav.modelSelector.title')) : null}
          appendIcon={"chevron-down"}
          appendIconSize={!isXS ? "12px" : "10px"}
          prependImageSize={!isXS ? "18px" : '20px'}
          text={allowsMultiple 
            ? (selectedModels.length > 0 ? renderModelImages() : t('nav.modelSelector.title'))
            : (!isXS ? (currentModel?.name || t('nav.modelSelector.title')) : t('nav.modelSelector.title'))
          }
          tooltip={t('nav.modelSelector.title')}
          hoverBackgroundColor={colors.solid_color_tertiary}
          isActive={isOpen}
          style={{
            paddingHorizontal: isXS ? 6 : 8,
          }}
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="DropdownMenuContent"
        style={{ minWidth: 200, paddingTop: pwaSafeAreaTop ? pwaSafeAreaTop + 8 : 0 }}
        align="end"
        sideOffset={8 + pwaTopOffset}
        avoidCollisions={true}
        collisionPadding={{ top: 16 + pwaTopOffset, bottom: 16, left: 16, right: 16 }}
      >
        <View style={styles.searchContainer}>
          <View style={styles.searchIconContainer}>
            <Icon 
              name="search" 
              size={14} 
              color={colors.text_color_secondary}
            />
          </View>
          <TextInput
            style={styles.searchInput}
            placeholder={t('nav.modelSelector.searchPlaceholder')}
            placeholderTextColor={colors.text_color_secondary}
            value={searchQuery}
            onChangeText={handleSearch}
          />
          {searchQuery.length > 0 && (
            <Pressable 
              style={styles.clearIconContainer}
              onPress={handleClearSearch}
            >
              <Icon 
                name="xmark" 
                size={14} 
                color={colors.text_color_secondary}
              />
            </Pressable>
          )}
        </View>

        {!searchQuery.trim() && <DropdownMenuLabel text={t('nav.modelSelector.featured')} />}
        {isSearching ? (
          <DropdownMenuItem
            title={t('nav.modelSelector.searching')}
            disabled
          />
        ) : searchError ? (
          <DropdownMenuItem
            title={t('nav.modelSelector.searchError')}
            disabled
          />
        ) : featuredModels.length === 0 && searchQuery.trim() ? (
          <DropdownMenuItem
            title={t('nav.modelSelector.noModels')}
            disabled
          />
        ) : (
          featuredModels.map((model) => {
            if (!model) return null;

            return (
              <DropdownMenuItem
                key={model.id}
                title={
                  <View style={styles.titleContainer}>
                    <Text>{model.name}</Text>
                    {isModelLockedForUser(user, model) && (
                      <Badge
                        text="PLUS"
                        onPress={() => handleModelSelect(model)}
                        icon="sparkles"
                        size="small"
                        style={styles.titleBadge}
                        isPlus
                      />
                    )}
                  </View>
                }
                onSelect={() => handleModelSelect(model)}
                isActive={selectedModelIds.includes(model.id)}
              />
            );
          })
        )}
        <DropdownMenuItem
          icon="cube"
          title={t('nav.modelSelector.viewAll')}
          onSelect={handleViewAllModels}
        />
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
};

ModelSelector.propTypes = {
  modelIds: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string)
  ]),
  onChange: PropTypes.func,
  allowsMultiple: PropTypes.bool,
};

ModelSelector.displayName = 'ModelSelector';

export default React.memo(ModelSelector);